import { useEffect, useRef } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { resetLereGroupAssignmentPromises } from 'lib/lere/lereGroupAssignment'
import { resetLereDecisionSessionState, type LereDynamicPricingAuthSession } from 'lib/lere/lereDynamicPricingLabSession'
import { selectLoggedIn } from 'selectors/accountSelectors'

interface UseLereAuthSessionResetsOptions {
  onMemberIdChange?: () => void
  onLoggedInChange?: (authSession: LereDynamicPricingAuthSession) => void
}

export default function useLereAuthSessionResets({
  onMemberIdChange,
  onLoggedInChange,
}: UseLereAuthSessionResetsOptions): void {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const memberId = useAppSelector(state => state.auth.account?.memberId)
  const previousMemberIdRef = useRef(memberId)
  const previousIsLoggedInRef = useRef(isLoggedIn)

  useEffect(() => {
    if (previousMemberIdRef.current === memberId) {
      return
    }

    previousMemberIdRef.current = memberId
    resetLereDecisionSessionState('loggedIn')
    resetLereGroupAssignmentPromises('loggedIn')
    onMemberIdChange?.()
  }, [memberId, onMemberIdChange])

  useEffect(() => {
    if (previousIsLoggedInRef.current === isLoggedIn) {
      return
    }

    previousIsLoggedInRef.current = isLoggedIn
    if (!isLoggedIn) {
      resetLereDecisionSessionState('loggedOut')
    }
    resetLereGroupAssignmentPromises(isLoggedIn ? 'loggedIn' : 'loggedOut')
    onLoggedInChange?.(isLoggedIn ? 'loggedIn' : 'loggedOut')
  }, [isLoggedIn, onLoggedInChange])
}
